import {
  faQuestionCircle
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Group, List, Stack, Text, Title } from "@mantine/core";
import React from "react";
import { connect } from "react-redux";
import { IState } from "../../../data/reducers/state";
import { IAssociation } from "../../../types/association/Association";
import { IPerson } from "../../../types/persons/person";
import { Privilege } from "../../../types/persons/privilege";
import AppointmentPaysTab from "./AppointmentPaysTab";

interface FinancePageProps {
  // Redux props
  self: IPerson;
  privilege: Privilege;
  // Direct props
  selectedAssociation: IAssociation | null;
  associations: IAssociation[];
}

/**
 * Finance administration content - now integrated into admin sidebar
 * Shows appointment pays management and ABA export functionality
 */
const FinancePage: React.FunctionComponent<FinancePageProps> = ({
  self,
  privilege,
  selectedAssociation,
  associations,
}) => {

  // State for BSB sync
  const canEdit = Privilege.canManage(privilege);

  if (!canEdit) {
    return (
      <Alert color="red" variant="light" radius="md">
        <Stack gap="xs">
          <Title order={5}>Access Denied</Title>
          <Text size="sm">You don't have permission to access the finance administration.</Text>
        </Stack>
      </Alert>
    );
  }

  if (!associations || associations.length === 0) {
    return (
      <Alert color="yellow" variant="light" radius="md">
        <Stack gap="xs">
          <Title order={5}>No Associations Available</Title>
          <Text size="sm">You need to be associated with an active organization to manage finances.</Text>
        </Stack>
      </Alert>
    );
  }

  if (!selectedAssociation) {
    return (
      <Alert color="blue" variant="light" radius="md">
        <Stack gap="xs">
          <Title order={5}>Select an Association</Title>
          <Text size="sm">Please select an association from the dropdown above to manage finances.</Text>
        </Stack>
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      {/* Main Finance Content - Appointment Pays */}
      <AppointmentPaysTab
        association={selectedAssociation}
        associations={associations.map(a => ({
          associationId: a._id,
          name: a.name
        }))}
        onAssociationChange={() => {
          // Association changes are now handled centrally
          // This callback is kept for compatibility but does nothing
        }}
        self={self}
      />

      {/* Help Section */}
      <Alert
        color="blue"
        radius="md"
      >
        <Group mb="md">
          <FontAwesomeIcon
            icon={faQuestionCircle}
            style={{ color: 'var(--mantine-color-blue-6)' }}
          />
          <Title order={6}>Finance Administration Help</Title>
        </Group>
        <List size="sm" spacing="xs">
          <List.Item>
            <strong>Pay Events:</strong> Create events to group appointment payments that need to be paid
          </List.Item>
          <List.Item>
            <strong>ABA Exports:</strong> Generate payment files from pay events for electronic bank transfers (users only see payment status)
          </List.Item>
          <List.Item>
            <strong>Security:</strong> The association's bank details are never stored and must be entered fresh for each export
          </List.Item>
          <List.Item>
            <strong>Validation:</strong> All bank details are validated including BSB lookup for major Australian banks
          </List.Item>
        </List>
      </Alert>
    </Stack>
  );
};

const mapStateToProps = (state: IState) => ({
  self: state.persons.self && state.persons.map[state.persons.self],
  privilege: state.auth.privilege,
});

export default connect(mapStateToProps)(FinancePage);
